import { useEffect, useMemo, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useRosterFunctions } from 'hooks';
import { omit } from 'lodash';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { NumericFormat } from 'react-number-format';

import {
  Scenario,
  WorkDemandRow,
  WorkDemandRowWithoutId
} from 'components/types';

import { Units } from 'components/enums';

import { getMaxDate } from 'utils/date';

import { createWorkDemand, updateWorkDemand } from 'RestAPIs/care-demand';

import { Button, DialogActions, TextField } from '@mui/material';
import Grid from '@mui/material/Grid';

import {
  DatePicker,
  LoadingOverlay,
  Select,
  showSnackbar,
  VirtualizedAutocomplete
} from 'components/shared';

import getAddEditWorkDemandSchema from '../schema';
import { formatWorkDemandDataForSave } from '../util';
import { WorkDemandForm } from './types';
import { StyledDialogMedium } from 'components/shared/Dialog/Dialog.styles';
import {
  GridCompact4,
  GridCompact6
} from 'components/shared/Grid/Grid.components';
import { GridContainerWithMargin } from './GridContainerWithMargin';

const defaultFormValues = {
  costCenterId: '',
  description: '',
  quantity: 1,
  directClientHours: 0,
  indirectClientHours: 0,
  organisationHours: 0,
  unit: '',
  validFrom: new Date(new Date().getFullYear(), 0, 1),
  validTo: getMaxDate(),
  functionId: {}
};

type Props = {
  isOpen: boolean;
  workDemand?: WorkDemandRow | WorkDemandRowWithoutId | null;
  handleClose: () => void;
  onSave: () => void;
  scenario: Scenario;
};

const AddEditWorkDemandDialog = ({
  isOpen,
  workDemand,
  handleClose,
  scenario,
  onSave
}: Props) => {
  const { t } = useTranslation('common');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const {
    rosterFunctions: rosterFunctionOptions,
    isLoading: isLoadingRosterFunctions,
    isOptionDisabled: isRosterFunctionDisabled,
    renderOption: renderRosterFunctionOption,
    getOptionLabel: getRosterFunctionLabel
  } = useRosterFunctions();

  const { control, handleSubmit, reset } = useForm<WorkDemandForm>({
    defaultValues: defaultFormValues,
    resolver: yupResolver(getAddEditWorkDemandSchema())
  });

  useEffect(() => {
    if (workDemand) {
      reset({
        ...omit(workDemand, ['timeframe', 'function']),
        validFrom: new Date(workDemand.timeframe.from),
        validTo: new Date(workDemand.timeframe.to),
        functionId: workDemand.function
      });
    } else {
      reset(defaultFormValues);
    }
  }, [workDemand]);

  const onSubmit: SubmitHandler<WorkDemandForm> = (data) => {
    const payload = formatWorkDemandDataForSave(data);

    setIsLoading(true);

    let savePromise;
    if (workDemand && 'id' in workDemand) {
      savePromise = updateWorkDemand({
        payload,
        scenarioId: scenario.scenarioId,
        workDemandId: workDemand.id
      });
    } else {
      savePromise = createWorkDemand({
        payload,
        scenarioId: scenario.scenarioId
      });
    }

    savePromise
      .then(() => {
        showSnackbar({
          severity: 'success',
          message: t('workDemand.workDemandSaved')
        });
        setIsLoading(false);
        onSave();
      })
      .catch(() => {
        showSnackbar({
          severity: 'error',
          message: t('somethingWentWrong')
        });
        setIsLoading(false);
      });
  };

  const unitOptions = useMemo(
    () =>
      Object.keys(Units).map((unit) => ({
        value: unit,
        label: t(`timeUnits.${unit}`)
      })),
    []
  );

  return (
    <StyledDialogMedium
      open={isOpen}
      title={
        workDemand && 'id' in workDemand
          ? t('workDemand.editWorkDemand')
          : t('workDemand.addWorkDemand')
      }
      handleClose={handleClose}
    >
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <GridContainerWithMargin>
          <GridCompact6>
            <Controller
              name="costCenterId"
              control={control}
              render={({ field, fieldState }) => (
                <Select
                  fullWidth
                  {...field}
                  onSelected={field.onChange}
                  label={t('costCenter')}
                  options={scenario.costCenters.map((costCenter) => ({
                    label: costCenter.name,
                    value: costCenter.id
                  }))}
                  error={fieldState.error}
                  valueProp={field.value}
                  required
                />
              )}
            />
          </GridCompact6>
          <GridCompact6>
            <Controller
              name="functionId"
              control={control}
              render={({ field, fieldState }) => (
                <VirtualizedAutocomplete
                  {...field}
                  onChange={(e, v) => {
                    field.onChange(v);
                  }}
                  optionLabelProp="description"
                  getOptionLabel={getRosterFunctionLabel}
                  getOptionDisabled={isRosterFunctionDisabled}
                  options={rosterFunctionOptions}
                  value={
                    workDemand
                      ? rosterFunctionOptions.find(
                          ({ id }) => id === workDemand?.function?.id
                        )
                      : undefined
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      ref={params.InputProps.ref}
                      label={t('function')}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message ?? null}
                      required
                    />
                  )}
                  renderOption={renderRosterFunctionOption}
                  isOptionEqualToValue={(option, value) => {
                    return option?.id === value?.id;
                  }}
                  autoHighlight
                  loading={isLoadingRosterFunctions}
                />
              )}
            />
          </GridCompact6>
        </GridContainerWithMargin>
        <GridContainerWithMargin>
          <Grid size={{ compact: 9 }}>
            <Controller
              name="description"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  fullWidth
                  {...field}
                  label={t('description')}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message ?? null}
                  required
                />
              )}
            />
          </Grid>
          <Grid size={{ compact: 3 }}>
            <Controller
              name="quantity"
              control={control}
              render={({ field, fieldState }) => (
                <NumericFormat
                  {...field}
                  customInput={TextField}
                  type="text"
                  onValueChange={({ value: v }) =>
                    field.onChange({ target: { value: v } })
                  }
                  label={t('quantity')}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message ?? null}
                  decimalSeparator=","
                  required
                  valueIsNumericString
                />
              )}
            />
          </Grid>
        </GridContainerWithMargin>
        <GridContainerWithMargin>
          <GridCompact4>
            <Controller
              name="directClientHours"
              control={control}
              render={({ field, fieldState }) => (
                <NumericFormat
                  {...field}
                  customInput={TextField}
                  type="text"
                  onValueChange={({ value: v }) =>
                    field.onChange({ target: { value: v } })
                  }
                  label={t('directHours')}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message ?? null}
                  decimalSeparator=","
                  required
                  valueIsNumericString
                />
              )}
            />
          </GridCompact4>
          <GridCompact4>
            <Controller
              name="indirectClientHours"
              control={control}
              render={({ field, fieldState }) => (
                <NumericFormat
                  {...field}
                  customInput={TextField}
                  type="text"
                  onValueChange={({ value: v }) =>
                    field.onChange({ target: { value: v } })
                  }
                  label={t('indirectHours')}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message ?? null}
                  decimalSeparator=","
                  required
                  valueIsNumericString
                />
              )}
            />
          </GridCompact4>
          <GridCompact4>
            <Controller
              name="organisationHours"
              control={control}
              render={({ field, fieldState }) => (
                <NumericFormat
                  {...field}
                  customInput={TextField}
                  type="text"
                  onValueChange={({ value: v }) =>
                    field.onChange({ target: { value: v } })
                  }
                  label={t('organizationHours')}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message ?? null}
                  decimalSeparator=","
                  required
                  valueIsNumericString
                />
              )}
            />
          </GridCompact4>
        </GridContainerWithMargin>
        <GridContainerWithMargin>
          <GridCompact4>
            <Controller
              name="unit"
              control={control}
              render={({ field, fieldState }) => (
                <Select
                  {...field}
                  fullWidth
                  onSelected={field.onChange}
                  label={t('unit')}
                  options={unitOptions}
                  error={fieldState.error}
                  valueProp={field.value}
                  required
                />
              )}
            />
          </GridCompact4>
          <GridCompact4>
            <Controller
              name="validFrom"
              control={control}
              render={({ field, fieldState }) => (
                <DatePicker
                  {...field}
                  date={field.value}
                  label={t('validFrom')}
                  error={fieldState.error}
                  required
                />
              )}
            />
          </GridCompact4>
          <GridCompact4>
            <Controller
              name="validTo"
              control={control}
              render={({ field, fieldState }) => (
                <DatePicker
                  {...field}
                  date={field.value}
                  label={t('validTo')}
                  error={fieldState.error}
                  required
                />
              )}
            />
          </GridCompact4>
        </GridContainerWithMargin>
        <DialogActions>
          <Button onClick={handleClose} color="secondary" variant="contained">
            {t('cancel')}
          </Button>
          <Button type="submit" variant="contained" disabled={isLoading}>
            {t('save')}
          </Button>
        </DialogActions>
        <LoadingOverlay loading={isLoading} />
      </form>
    </StyledDialogMedium>
  );
};

export default AddEditWorkDemandDialog;
